import { BrandType, NoddiAsyncError } from 'noddi-types';
import { ApiErrorMessage, ErrorCodes, NoddiButton, NoddiLinearProgressLoader, NoddiOtp } from 'noddi-ui-web';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { isLocal } from '../../constants/environment';
import type { SMSLoginStepRenderer } from './smsLoginStep';
import { useSendVerificationSms } from './useSendVerificationSms';

type Props = {
  /** The number the code was sent to, as sent to the API. */
  phoneNumber: string;
  onSubmitCode: (code: string) => void;
  isLoading: boolean;
  verificationSuccess: boolean;
  error: NoddiAsyncError | null;
  translations: {
    enterCode: string;
    confirm: string;
    /** Footer submit when `renderStep` places it away from the code field; falls back to `confirm`. */
    logIn?: string;
    didntReceiveSms: string;
    resend: string;
    sentTo: string;
    /** Back to the phone step, beside the sent-to line or in the page layout. */
    changeNumber?: string;
    /** Page layout only; falls back to `resend`. */
    sendNewCode?: string;
  };
  errorCodes: ErrorCodes;
  brandName: BrandType;
  /** Places the confirm button outside the form; the page then owns the heading and the sent-to line. */
  renderStep?: SMSLoginStepRenderer;
  /** Back to the phone step, so a mistyped number can be fixed without leaving the flow. */
  onChangeNumber?: () => void;
};

export const VerificationCodeForm = ({
  phoneNumber,
  onSubmitCode,
  isLoading,
  verificationSuccess,
  error,
  translations,
  errorCodes,
  brandName,
  renderStep,
  onChangeNumber
}: Props) => {
  const forceVerifySMS = typeof import.meta !== 'undefined' && import.meta.env?.VITE_FORCE_VERIFY_SMS === 'true';
  const skipVerification = isLocal && !forceVerifySMS;
  const [verificationCode, setVerificationCode] = useState(skipVerification ? '123456' : '');
  const {
    send: resend,
    isSending: isResending,
    errorFor: resendErrorFor
  } = useSendVerificationSms({ brandName, isResend: true });
  const resendError = resendErrorFor(phoneNumber);

  useEffect(() => {
    if (skipVerification) {
      onSubmitCode(verificationCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount to auto-submit the local dev code; onSubmitCode/verificationCode deps would resubmit on change
  }, []);

  const verificationCodeIsValid = verificationCode.length === 6;

  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }, []);

  // Resending leaves focus in the code field, so a phone keeps the keyboard and its suggestion of the new code.
  const keepCodeFieldFocus = (event: MouseEvent) => event.preventDefault();

  const verificationFeedback = (
    <>
      {isLoading && <NoddiLinearProgressLoader />}
      {error && verificationCode && <ApiErrorMessage errorCodes={errorCodes} error={error} />}
    </>
  );

  const renderOtp = (size: 'default' | 'large') => (
    <NoddiOtp
      value={verificationCode}
      onChange={setVerificationCode}
      onComplete={onSubmitCode}
      maxLength={6}
      size={size}
      aria-label={translations.enterCode}
      ref={ref}
    />
  );

  const resendRow = (
    <>
      <div className='flex items-center justify-between'>
        <p>{translations.didntReceiveSms}</p>
        <NoddiButton
          onClick={() => void resend(phoneNumber)}
          onMouseDown={keepCodeFieldFocus}
          loading={isResending}
          variant='link'
          size='small'
        >
          {translations.resend}
        </NoddiButton>
      </div>
      {resendError && <ApiErrorMessage errorCodes={errorCodes} error={resendError} />}
    </>
  );

  if (renderStep) {
    return renderStep({
      content: (
        <div className='flex w-full animate-in flex-col gap-4 duration-500 fade-in slide-in-from-left-4'>
          {renderOtp('large')}
          {(isLoading || (error && verificationCode)) && <div className='flex gap-2'>{verificationFeedback}</div>}
          <div className='flex flex-wrap items-center gap-5 text-sm font-medium'>
            {onChangeNumber && translations.changeNumber && (
              <NoddiButton variant='link' size='small' className='px-0' onClick={onChangeNumber}>
                {translations.changeNumber}
              </NoddiButton>
            )}
            <NoddiButton
              variant='link'
              size='small'
              className='px-0'
              loading={isResending}
              onClick={() => void resend(phoneNumber)}
              onMouseDown={keepCodeFieldFocus}
            >
              {translations.sendNewCode ?? translations.resend}
            </NoddiButton>
          </div>
          {resendError && <ApiErrorMessage errorCodes={errorCodes} error={resendError} />}
        </div>
      ),
      submit: (
        <NoddiButton
          endIcon='ArrowRight'
          disabled={!verificationCodeIsValid || verificationSuccess}
          loading={isLoading}
          onClick={() => onSubmitCode(verificationCode)}
        >
          {translations.logIn ?? translations.confirm}
        </NoddiButton>
      )
    });
  }

  return (
    <div className='flex w-full animate-in flex-col gap-3 duration-500 fade-in slide-in-from-left-4'>
      {translations.enterCode && <p className='text-sm'>{translations.enterCode}</p>}

      <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
        {renderOtp('default')}

        {/* Feedback sits between the code and the full-width button on phones, under the row on wider screens. */}
        <div className='flex gap-2 sm:hidden'>{verificationFeedback}</div>

        <NoddiButton
          className='w-full sm:w-fit'
          disabled={!verificationCodeIsValid || isLoading || verificationSuccess}
          onClick={() => onSubmitCode(verificationCode)}
        >
          {translations.confirm}
        </NoddiButton>
      </div>
      <div className='hidden gap-2 sm:flex'>{verificationFeedback}</div>

      <div className='flex flex-wrap items-center gap-x-3'>
        <p className='text-xs text-gray-500'>
          {translations.sentTo} {phoneNumber}
        </p>
        {onChangeNumber && translations.changeNumber && (
          <NoddiButton variant='link' size='small' className='px-0' onClick={onChangeNumber}>
            {translations.changeNumber}
          </NoddiButton>
        )}
      </div>

      {resendRow}
    </div>
  );
};
